/**
 * StatCard — canonical KPI stat card used across all dashboards.
 * v22 rebuild: internally renders {@link MetricCardHeader}.
 *
 * Sept 2026 · global number-coloring convention supersedes the prior
 * "Directive §2.A" accessible blue/orange/gray scale — trend badges now
 * render green (up/info) / red (down/warning) / dark-grey (neutral),
 * matching lib/numberColor.js everywhere else in the app.
 *
 * Public props kept for back-compat:
 *   label      — string, overline eyebrow
 *   value      — string | ReactNode
 *   caption    — string | ReactNode (secondary description under value)
 *   trend      — "up" | "down" | "warning" | "info" | "neutral"
 *   trendText  — string inside the badge (e.g. "+2.4%")
 *   action     — ReactNode rendered top-right (edit button etc.)
 *   href       — optional link (turns the card into a card-hover interactive)
 *   dense      — bool (legacy — retained for API stability; density is
 *                now driven globally by DensityContext)
 *   testId
 */
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import MetricCardHeader, { DeltaBadge } from "@/components/dashboard/MetricCardHeader";

/* ── Trend → number-color tone mapping ─────────────────────────────
 *   up / info       → green  (--success) — profit/positive
 *   down / warning  → red    (--danger)  — loss/negative
 *   neutral         → dark grey (--zero) — flat/zero
 * `data-tone` always emits "pos" | "neg" | "zero" for query selectors.
 * ---------------------------------------------------------------- */
const TREND_TONE = {
    up:      { key: "pos",  fg: "hsl(var(--success))", bg: "hsl(var(--success) / 0.10)", Icon: ArrowUpRight },
    info:    { key: "pos",  fg: "hsl(var(--success))", bg: "hsl(var(--success) / 0.10)", Icon: ArrowUpRight },
    down:    { key: "neg",  fg: "hsl(var(--danger))",  bg: "hsl(var(--danger) / 0.10)",  Icon: ArrowDownRight },
    warning: { key: "neg",  fg: "hsl(var(--danger))",  bg: "hsl(var(--danger) / 0.10)",  Icon: Minus },
    neutral: { key: "zero", fg: "hsl(var(--zero))",    bg: "hsl(var(--zero) / 0.10)",    Icon: Minus },
};

export default function StatCard({
    label, value, caption, trend, trendText, action, href, testId, className = "",
    // `dense` retained for back-compat; layout density is now global.
    // eslint-disable-next-line no-unused-vars
    dense,
}) {
    const Wrapper = href ? Link : "div";
    const wrapperProps = href ? { to: href } : {};

    // Compose the trailing slot: existing action (edit/info icon) + trend badge.
    const trailing = (action || (trend && trendText))
        ? (
            <div className="flex items-center gap-1.5">
                {trend && trendText && <TrendBadge tone={trend} text={trendText} />}
                {action && <span className="opacity-70 group-hover:opacity-100 transition-opacity">{action}</span>}
            </div>
        )
        : null;

    return (
        <Wrapper
            {...wrapperProps}
            data-testid={testId}
            className={
                "group flex flex-col p-6 density-compact:p-3.5 bg-card rounded-lg border border-border transition duration-200 " +
                "hover:bg-muted/40 hover:shadow-md hover:-translate-y-0.5 " + (href ? "cursor-pointer " : "") + className
            }
        >
            <MetricCardHeader
                title={label}
                value={value}
                subtitle={caption}
                trailing={trailing}
                testid={testId ? `${testId}-header` : undefined}
                className="mb-0"
            />
        </Wrapper>
    );
}

/**
 * TrendBadge — data-scale coloured pill re-exported for legacy callers.
 * Uses the same palette as MetricCardHeader's DeltaBadge.
 */
export function TrendBadge({ tone = "neutral", text }) {
    const t = TREND_TONE[tone] || TREND_TONE.neutral;
    const Icon = t.Icon;
    return (
        <span
            data-tone={t.key}
            data-trend={tone}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-caption font-heading"
            style={{ background: t.bg, color: t.fg, fontFeatureSettings: '"pnum" 1, "zero" 0' }}
        >
            {Icon && <Icon width={14} height={14} strokeWidth={1.75} />}
            {text}
        </span>
    );
}

/**
 * StatusBadge — kept in the accessible palette (no raw red/green).
 */
export function StatusBadge({ tone = "neutral", children, testId }) {
    const t = TREND_TONE[tone] || TREND_TONE.neutral;
    return (
        <span
            data-testid={testId}
            data-tone={t.key}
            data-status={tone}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-caption font-heading"
            style={{ background: t.bg, color: t.fg }}
        >
            {children}
        </span>
    );
}

/* Re-export the shared DeltaBadge for callers that want numeric-driven
 * delta badges without the legacy `trend` string API. */
export { DeltaBadge };
